import { useCallback, useEffect, useMemo, useState } from 'react';
import { COURSES, COURSE_RANK, DIETARY_TAGS, MENU_TYPES } from '@lemontaigu/shared';
import type { CourseSlug, DietaryTag, Dish, DishInput, MenuSnapshot, MenuTypeSlug, SnapshotItem } from '@lemontaigu/shared';
import { bulkSetActive, createDish, listDishes, softDeleteDish, updateDish } from '../data/dishes.js';
import { panelDishes, setDayServings } from '../data/servings.js';
import { listFuture, listHistory } from '../data/snapshots.js';
import { addDaysIso, addMonthsIso, frDate, todayIso } from '../data/util.js';
import { FrDateInput } from '../../ui/FrDateInput.js';

const STD_COURSES: CourseSlug[] = ['entree', 'plat-principal', 'dessert'];
const courseLabel = (slug: string): string => COURSES.find((c) => c.slug === slug)?.label ?? (slug || '—');
const menuTypeLabel = (slug: string): string => MENU_TYPES.find((m) => m.slug === slug)?.label ?? slug;
const rankOf = (course: string | null): number => (course ? COURSE_RANK[course as CourseSlug] ?? 99 : 99);

/* ── Snapshot list (read-only past day / last served) ───────────────────────── */
function MenuList({ items, empty }: { items: SnapshotItem[]; empty: string }) {
  if (!items || items.length === 0) return <p className="me-mdj-empty">{empty}</p>;
  // Course order (Entrée · Plat principal · Dessert …), not the order the rows
  // came back in — a menu reads in the order it is served. Display-only: the
  // snapshot itself is untouched, so R5's append-only history still holds.
  const ordered = [...items].sort((a, b) => rankOf(a.course) - rankOf(b.course));
  return (
    <ul className="me-mdj-list">
      {ordered.map((it, i) => (
        <li key={i}>
          {it.course ? (
            <span className={'me-course me-course-' + it.course}>{it.courseLabel || courseLabel(it.course)}</span>
          ) : null}
          <span className="me-mdj-text">
            <span className="me-mdj-name">{it.title}</span>
            {it.description ? <span className="me-mdj-desc">{it.description}</span> : null}
          </span>
          {it.price ? <span className="me-mdj-price">{it.price}</span> : null}
        </li>
      ))}
    </ul>
  );
}

/* ── Today / future editor — draft the whole day, save as a unit ────────────── */
type DaySlot = { name: string; desc: string };
type DayDraft = Record<string, DaySlot>;
const emptyDayDraft = (): DayDraft => {
  const d: DayDraft = {};
  for (const c of STD_COURSES) d[c] = { name: '', desc: '' };
  return d;
};

function TodayEditor({
  draft,
  namesByCourse,
  busy,
  dirty,
  onSlot,
  onSave,
  onClear,
}: {
  draft: DayDraft;
  namesByCourse: Record<string, { id: number; name: string }[]>;
  busy: boolean;
  dirty: boolean;
  onSlot: (course: CourseSlug, patch: Partial<DaySlot>) => void;
  onSave: () => void;
  onClear: () => void;
}) {
  return (
    <div className="me-today-edit">
      {STD_COURSES.map((course) => {
        const slot = draft[course] ?? { name: '', desc: '' };
        const listId = 'me-names-' + course;
        return (
          <div className="me-today-row" key={course}>
            <span className={'me-course me-course-' + course}>{courseLabel(course)}</span>
            <div className="me-today-fields">
              <input
                list={listId}
                className="me-today-name"
                placeholder={'+ ' + courseLabel(course)}
                value={slot.name}
                disabled={busy}
                onChange={(e) => onSlot(course, { name: e.target.value })}
              />
              <input
                className="me-today-desc"
                placeholder="Description…"
                value={slot.desc}
                disabled={busy}
                onChange={(e) => onSlot(course, { desc: e.target.value })}
              />
              <datalist id={listId}>
                {(namesByCourse[course] || []).map((n) => (
                  <option key={n.id} value={n.name} />
                ))}
              </datalist>
            </div>
          </div>
        );
      })}
      <div className="me-today-actions">
        <button className="me-btn me-btn-primary" disabled={busy || !dirty} onClick={onSave}>
          {busy ? '…' : dirty ? 'Enregistrer' : 'Menu du jour enregistré'}
        </button>
        <button className="me-btn me-btn-ghost" disabled={busy} onClick={onClear} title="Vider les trois plats">
          Effacer
        </button>
      </div>
      <p className="me-today-hint">
        Entrez les plats puis « Enregistrer ». Pour supprimer ce Menu du Jour : « Effacer » les champs puis
        « Enregistrer ».
      </p>
    </div>
  );
}

/* ── Form helpers ───────────────────────────────────────────────────────────── */
interface FormState {
  title: string;
  description: string;
  price: string;
  allergens: string;
  dietary: string[];
  isCourseNote: boolean;
  isActive: boolean;
  menuTypes: string[];
  course: string;
  position: number;
}
const blankForm = (seed: Partial<FormState> = {}): FormState => ({
  title: '',
  description: '',
  price: '',
  allergens: '',
  dietary: [],
  isCourseNote: false,
  isActive: true,
  menuTypes: [],
  course: '',
  position: 0,
  ...seed,
});
const dishToForm = (d: Dish): FormState => ({
  title: d.name,
  description: d.description,
  price: d.price,
  allergens: d.allergens,
  dietary: [...d.dietaryTags],
  isCourseNote: d.isCourseNote,
  isActive: d.isActive,
  menuTypes: [...d.menuTypes],
  course: d.course ?? '',
  position: d.coursePosition,
});
const formToInput = (f: FormState): DishInput => ({
  name: f.title.trim(),
  description: f.description,
  price: f.price,
  allergens: f.allergens,
  dietaryTags: f.dietary as DietaryTag[],
  isCourseNote: f.isCourseNote,
  isActive: f.isActive,
  menuTypes: f.menuTypes as MenuTypeSlug[],
  course: (f.course || null) as CourseSlug | null,
  coursePosition: f.position,
});

/* ── App ────────────────────────────────────────────────────────────────────── */
export function Editor({ onPrint }: { onPrint?: () => void } = {}) {
  const today = todayIso();
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [history, setHistory] = useState<MenuSnapshot[]>([]);
  const [panelList, setPanelList] = useState<Dish[]>([]);
  const [histFrom, setHistFrom] = useState(() => addMonthsIso(today, -3));
  const [histTo, setHistTo] = useState('');
  const [panelDateIso, setPanelDateIso] = useState(today);
  const [dayDraft, setDayDraft] = useState<DayDraft>(emptyDayDraft);
  const [dayDirty, setDayDirty] = useState(false);

  const [search, setSearch] = useState('');
  const [fMenuType, setFMenuType] = useState<string>('carte');
  const [fCourse, setFCourse] = useState('');
  const [fActive, setFActive] = useState('');

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(blankForm());
  const [editing, setEditing] = useState(false);
  const [isNew, setIsNew] = useState(false);

  const [checked, setChecked] = useState<Set<number>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const flash = useCallback((type: 'ok' | 'err', text: string) => {
    setMsg({ type, text });
    window.clearTimeout((flash as unknown as { _t?: number })._t);
    (flash as unknown as { _t?: number })._t = window.setTimeout(() => setMsg(null), 4000);
  }, []);

  const panelIsToday = panelDateIso === today;
  const panelIsPast = panelDateIso < today;
  const panelSnapshot = useMemo(() => history.find((h) => h.date === panelDateIso) ?? null, [history, panelDateIso]);

  const historyRows = useMemo(
    () => history.filter((h) => h.date >= histFrom && (!histTo || h.date <= histTo)),
    [history, histFrom, histTo],
  );

  const loadPanel = useCallback(
    async (date: string) => {
      if (date < today) {
        setPanelList([]);
        return;
      }
      try {
        setPanelList(await panelDishes(date));
      } catch {
        /* non-fatal */
      }
    },
    [today],
  );

  const refreshDishes = useCallback(async () => {
    try {
      setDishes(await listDishes());
    } catch (e) {
      flash('err', 'Chargement : ' + (e as Error).message);
    }
  }, [flash]);

  const refreshHistory = useCallback(() => {
    Promise.all([listHistory(400), listFuture(today)])
      .then(([past, future]) =>
        setHistory([...future, ...past].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))),
      )
      .catch(() => undefined);
  }, [today]);

  useEffect(() => {
    void refreshDishes();
    refreshHistory();
  }, [refreshDishes, refreshHistory]);

  useEffect(() => {
    void loadPanel(panelDateIso);
  }, [panelDateIso, loadPanel]);

  // Seed the day draft from the served dishes whenever the panel (re)loads.
  useEffect(() => {
    const draft = emptyDayDraft();
    for (const c of STD_COURSES) {
      const d = panelList.find((x) => x.course === c);
      if (d) draft[c] = { name: d.name, desc: d.description };
    }
    setDayDraft(draft);
    setDayDirty(false);
  }, [panelList]);

  /* ── Filtering + sorting ─────────────────────────────────────────────────── */
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = dishes.filter((d) => {
      if (q && !d.name.toLowerCase().includes(q)) return false;
      if (fMenuType && !d.menuTypes.includes(fMenuType as MenuTypeSlug)) return false;
      if (fCourse && d.course !== fCourse) return false;
      if (fActive === 'active' && !d.isActive) return false;
      if (fActive === 'inactive' && d.isActive) return false;
      return true;
    });
    list.sort((a, b) =>
      rankOf(a.course) !== rankOf(b.course)
        ? rankOf(a.course) - rankOf(b.course)
        : a.coursePosition !== b.coursePosition
          ? a.coursePosition - b.coursePosition
          : a.name.localeCompare(b.name),
    );
    return list;
  }, [dishes, search, fMenuType, fCourse, fActive]);

  const selected = dishes.find((d) => d.id === selectedId) ?? null;

  const namesByCourse = useMemo(() => {
    const map: Record<string, { id: number; name: string }[]> = {};
    dishes.forEach((d) => {
      if (!d.name || !d.course || !d.menuTypes.includes('daily')) return;
      const arr = (map[d.course] = map[d.course] || []);
      if (!arr.some((x) => x.name.toLowerCase() === d.name.toLowerCase())) arr.push({ id: d.id, name: d.name });
    });
    Object.values(map).forEach((a) => a.sort((x, y) => x.name.localeCompare(y.name)));
    return map;
  }, [dishes]);

  /* ── Menu du Jour quick actions (dish_servings) ──────────────────────────── */
  const withBusy = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      flash('err', 'Échec : ' + (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const setDaySlot = (course: CourseSlug, patch: Partial<DaySlot>) => {
    setDayDraft((prev) => ({ ...prev, [course]: { ...(prev[course] ?? { name: '', desc: '' }), ...patch } }));
    setDayDirty(true);
  };
  const clearDay = () => {
    setDayDraft(emptyDayDraft());
    setDayDirty(true);
  };

  // Save the whole Menu du Jour as a unit: resolve each filled slot to a dish
  // (reuse by name+course, else create + update its description), then atomically
  // replace the day's servings via set_day_servings (empty = cleared). All or none.
  const saveDay = () =>
    withBusy(async () => {
      if (panelIsPast) return;
      // Preserve any served daily dish on a non-standard course — the form only
      // manages entrée / plat / dessert.
      const targetIds: number[] = panelList
        .filter((d) => d.course != null && !STD_COURSES.includes(d.course))
        .map((d) => d.id);

      for (const course of STD_COURSES) {
        const slot = dayDraft[course] ?? { name: '', desc: '' };
        const name = slot.name.trim();
        if (!name) continue; // empty slot → not served
        const desc = slot.desc;
        const lc = name.toLowerCase();

        const served = panelList.find((x) => x.course === course);
        const dish: Dish | undefined =
          served && served.name.toLowerCase() === lc
            ? served
            : dishes.find((x) => x.course === course && !x.deletedAt && x.name.toLowerCase() === lc);

        if (dish) {
          const patch: Partial<DishInput> = {};
          if (!dish.menuTypes.includes('daily'))
            patch.menuTypes = Array.from(new Set([...dish.menuTypes, 'daily'])) as MenuTypeSlug[];
          if (!dish.isActive) patch.isActive = true;
          if (dish.description !== desc) patch.description = desc;
          if (Object.keys(patch).length) upsertDish(await updateDish(dish.id, patch));
          targetIds.push(dish.id);
        } else {
          const created = await createDish({ name, description: desc, menuTypes: ['daily'], course, isActive: true });
          upsertDish(created);
          targetIds.push(created.id);
        }
      }

      const n = await setDayServings(panelDateIso, targetIds);
      await loadPanel(panelDateIso);
      refreshHistory();
      flash('ok', n === 0 ? 'Menu du jour vidé.' : 'Menu du jour enregistré (' + n + ' plat' + (n > 1 ? 's' : '') + ').');
    });

  /* ── Catalogue CRUD ──────────────────────────────────────────────────────── */
  const upsertDish = (saved: Dish) =>
    setDishes((prev) => {
      const i = prev.findIndex((d) => d.id === saved.id);
      if (i === -1) return [...prev, saved];
      const copy = prev.slice();
      copy[i] = saved;
      return copy;
    });

  const selectRow = (d: Dish) => {
    setSelectedId(d.id);
    setForm(dishToForm(d));
    setEditing(false);
    setIsNew(false);
  };

  const onEffacer = () => {
    const baseMenuTypes = selected ? selected.menuTypes : fMenuType ? [fMenuType] : [];
    const baseCourse = selected ? selected.course ?? '' : fCourse;
    setForm(blankForm({ menuTypes: [...baseMenuTypes], course: baseCourse }));
    setSelectedId(null);
    setIsNew(true);
    setEditing(true);
  };

  const onEnregistrer = () =>
    withBusy(async () => {
      if (!form.title.trim()) {
        flash('err', 'Le nom du plat est obligatoire.');
        return;
      }
      const input = formToInput(form);
      const saved = isNew ? await createDish(input) : await updateDish(selectedId as number, input);
      upsertDish(saved);
      setSelectedId(saved.id);
      setForm(dishToForm(saved));
      setEditing(false);
      setIsNew(false);
      await loadPanel(panelDateIso);
      flash('ok', isNew ? 'Plat créé.' : 'Modifications enregistrées.');
    });

  const onDupliquer = () =>
    withBusy(async () => {
      if (!selected) return;
      const saved = await createDish({ ...formToInput(dishToForm(selected)), name: selected.name + ' (copie)' });
      upsertDish(saved);
      selectRow(saved);
      setEditing(true);
      flash('ok', 'Plat dupliqué. Ajustez puis enregistrez.');
    });

  const onSupprimer = () =>
    withBusy(async () => {
      if (!selected) return;
      if (!window.confirm('Supprimer « ' + selected.name + ' » ? (récupérable)')) return;
      const delId = selected.id;
      await softDeleteDish(delId);
      setDishes((prev) => prev.filter((d) => d.id !== delId));
      setSelectedId(null);
      setForm(blankForm());
      setEditing(false);
      setIsNew(false);
      await loadPanel(panelDateIso);
      flash('ok', 'Plat supprimé.');
    });

  const toggleActive = (d: Dish) =>
    withBusy(async () => {
      const saved = await updateDish(d.id, { isActive: !d.isActive });
      upsertDish(saved);
      await loadPanel(panelDateIso);
      if (selectedId === d.id) setForm((f) => ({ ...f, isActive: saved.isActive }));
    });

  // Inline per-course rank edit from the table (saves on blur / Enter).
  const setRank = (d: Dish, value: number) => {
    if (!Number.isFinite(value) || value === d.coursePosition) return;
    void withBusy(async () => {
      const saved = await updateDish(d.id, { coursePosition: value });
      upsertDish(saved);
      if (selectedId === d.id) setForm((f) => ({ ...f, position: saved.coursePosition }));
    });
  };

  /* ── Bulk ────────────────────────────────────────────────────────────────── */
  const toggleCheck = (id: number) =>
    setChecked((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allVisibleChecked = rows.length > 0 && rows.every((d) => checked.has(d.id));
  const toggleCheckAll = () =>
    setChecked((prev) => {
      const n = new Set(prev);
      if (allVisibleChecked) rows.forEach((d) => n.delete(d.id));
      else rows.forEach((d) => n.add(d.id));
      return n;
    });
  const bulkActive = (active: boolean) =>
    withBusy(async () => {
      const ids = [...checked];
      if (!ids.length) return;
      const updated = await bulkSetActive(ids, active);
      updated.forEach(upsertDish);
      setChecked(new Set());
      await loadPanel(panelDateIso);
      flash('ok', updated.length + ' plat(s) mis à jour.');
    });

  /* ── Form field setters ──────────────────────────────────────────────────── */
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInArray = (k: 'dietary' | 'menuTypes', val: string) =>
    setForm((f) => {
      const arr = f[k].includes(val) ? f[k].filter((x) => x !== val) : [...f[k], val];
      return { ...f, [k]: arr };
    });

  const formActive = editing || isNew;
  const showForm = !!selected || isNew;

  /* ── Render ──────────────────────────────────────────────────────────────── */
  return (
    <div className="me-wrap">
      {msg && <div className={'me-flash me-flash-' + msg.type}>{msg.text}</div>}

      {/* Menu du Jour + History, side by side */}
      <div className="me-mdj-hist-row">
      <div className="me-mdj">
        {/* Shortcut to the print view (operator request 2026-08-04). The tab
            already exists under « Restaurant » — this saves the round trip
            through the menu once the day's menu is entered. Routing belongs to
            the composition root, so the editor takes a callback rather than
            touching the hash itself; no callback, no button. */}
        {onPrint && (
          <div className="me-mdj-actions">
            <button type="button" className="me-btn me-mdj-print" onClick={onPrint}>
              Imprimer
            </button>
          </div>
        )}
        <div className="me-mdj-card me-mdj-today">
          <div className="me-mdj-head">
            <h2>Menu du Jour{panelIsToday ? ' — aujourd’hui' : ''}</h2>
            <span className="me-mdj-datectl">
              <button
                className="me-btn me-btn-ghost me-mdj-navbtn"
                onClick={() => setPanelDateIso((d) => addDaysIso(d, -1))}
                aria-label="Jour précédent"
              >
                ◀
              </button>
              <FrDateInput className="me-mdj-datepick" value={panelDateIso} onChange={setPanelDateIso} />
              <button
                className="me-btn me-btn-ghost me-mdj-navbtn"
                onClick={() => setPanelDateIso((d) => addDaysIso(d, 1))}
                aria-label="Jour suivant"
              >
                ▶
              </button>
              {!panelIsToday && (
                <button className="me-btn me-btn-ghost me-mdj-todaybtn" onClick={() => setPanelDateIso(today)}>
                  Aujourd’hui
                </button>
              )}
            </span>
          </div>
          {panelIsPast ? (
            <>
              <p className="me-mdj-frozen">🔒 Menu passé — lecture seule (non modifiable).</p>
              <MenuList items={panelSnapshot ? panelSnapshot.items : []} empty="Aucun menu archivé pour cette date." />
            </>
          ) : (
            <TodayEditor
              draft={dayDraft}
              namesByCourse={namesByCourse}
              busy={busy}
              dirty={dayDirty}
              onSlot={setDaySlot}
              onSave={saveDay}
              onClear={clearDay}
            />
          )}
        </div>
      </div>

      {/* History (always expanded). The inner wrapper is what the row layout
          positions: it is taken out of flow so History's content cannot inflate
          the row, letting Menu du Jour alone set the height and the list scroll
          within it (editor.css, .me-mdj-hist-row .me-hist-inner). */}
      <div className="me-hist">
        <div className="me-hist-inner">
        <h2 className="me-hist-title">Historique des menus ({historyRows.length})</h2>
        <div className="me-hist-range">
          <label>
            Du <FrDateInput value={histFrom} onChange={setHistFrom} />
          </label>
          <label>
            au <FrDateInput value={histTo} onChange={setHistTo} />
          </label>
        </div>
        <div className="me-hist-list">
          {historyRows.length === 0 && <p className="me-mdj-empty">Aucun menu sur cette période.</p>}
          {historyRows.map((h) => (
            <div className="me-hist-day" key={h.date}>
              <div className="me-hist-day-head">
                <strong>{frDate(h.date)}</strong>
                {h.date > today && <span className="me-tag">à venir</span>}
              </div>
              <MenuList items={h.items} empty="—" />
            </div>
          ))}
        </div>
        </div>
      </div>
      </div>

      {/* Toolbar */}
      <div className="me-toolbar">
        <div className="me-filters">
          <select value={fMenuType} onChange={(e) => setFMenuType(e.target.value)}>
            <option value="">Tous les menus</option>
            {MENU_TYPES.map((m) => (
              <option key={m.slug} value={m.slug}>
                {m.label}
              </option>
            ))}
          </select>
          <select value={fCourse} onChange={(e) => setFCourse(e.target.value)}>
            <option value="">Tous les plats</option>
            {COURSES.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.label}
              </option>
            ))}
          </select>
          <select value={fActive} onChange={(e) => setFActive(e.target.value)}>
            <option value="">Actifs + inactifs</option>
            <option value="active">Actifs seulement</option>
            <option value="inactive">Inactifs seulement</option>
          </select>
        </div>
        <input
          className="me-search"
          type="search"
          placeholder="Rechercher un plat…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="me-count">{rows.length} plat(s)</div>

      {checked.size > 0 && (
        <div className="me-bulkbar">
          <strong>{checked.size}</strong> sélectionné(s) :
          <button className="me-btn" disabled={busy} onClick={() => bulkActive(true)}>
            Activer
          </button>
          <button className="me-btn" disabled={busy} onClick={() => bulkActive(false)}>
            Désactiver
          </button>
          <button className="me-btn me-btn-ghost" onClick={() => setChecked(new Set())}>
            Effacer la sélection
          </button>
        </div>
      )}

      {/* Table */}
      <div className="me-table-wrap">
        <table className="me-table">
          <thead>
            <tr>
              <th className="me-c-check">
                <input type="checkbox" checked={allVisibleChecked} onChange={toggleCheckAll} />
              </th>
              <th className="me-c-rank">Rang</th>
              <th>Nom</th>
              <th>Menus</th>
              <th>Plat</th>
              <th>Prix</th>
              <th className="me-c-active">Actif</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="me-empty">
                  Aucun plat ne correspond.
                </td>
              </tr>
            )}
            {rows.map((d) => (
              <tr
                key={d.id}
                className={(selectedId === d.id ? 'is-selected ' : '') + (d.isActive ? '' : 'is-inactive')}
                onClick={() => selectRow(d)}
              >
                <td className="me-c-check" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={checked.has(d.id)} onChange={() => toggleCheck(d.id)} />
                </td>
                <td className="me-c-rank" onClick={(e) => e.stopPropagation()}>
                  {d.course ? (
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className="me-rank-input"
                      key={d.id + ':' + d.coursePosition}
                      defaultValue={d.coursePosition}
                      disabled={busy}
                      aria-label={'Rang dans ' + courseLabel(d.course)}
                      onBlur={(e) => setRank(d, Number(e.target.value) || 0)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      }}
                    />
                  ) : (
                    <span className="me-muted">—</span>
                  )}
                </td>
                <td className="me-c-name">
                  <span className="me-c-title">
                    {d.name || <em>(sans titre)</em>}
                    {d.isCourseNote && <span className="me-tag">note</span>}
                  </span>
                  {d.description && <span className="me-c-desc">{d.description}</span>}
                </td>
                <td>
                  {d.menuTypes.map((m) => (
                    <span key={m} className={'me-badge me-badge-' + m}>
                      {menuTypeLabel(m)}
                    </span>
                  ))}
                </td>
                <td>
                  {d.course ? (
                    <span className={'me-course me-course-' + d.course}>{courseLabel(d.course)}</span>
                  ) : (
                    <span className="me-muted">—</span>
                  )}
                </td>
                <td>{d.price || <span className="me-muted">—</span>}</td>
                <td className="me-c-active" onClick={(e) => e.stopPropagation()}>
                  <button
                    className={'me-switch ' + (d.isActive ? 'on' : 'off')}
                    disabled={busy}
                    onClick={() => toggleActive(d)}
                  >
                    {d.isActive ? 'Oui' : 'Non'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail form */}
      <div className={'me-form ' + (showForm ? '' : 'is-hidden')}>
        <div className="me-form-head">
          <h2>{isNew ? 'Nouveau plat' : selected ? 'Détail du plat' : 'Aucune sélection'}</h2>
          {!formActive && selected && <span className="me-locked">🔒 lecture seule</span>}
        </div>

        <div className="me-actions me-actions-top">
          <button className="me-btn me-btn-new" onClick={onEffacer}>
            + Nouveau plat
          </button>
          {!formActive && selected && (
            <button className="me-btn me-btn-primary" onClick={() => setEditing(true)}>
              Modifier
            </button>
          )}
          {formActive && (
            <button className="me-btn me-btn-primary" disabled={busy} onClick={onEnregistrer}>
              {busy ? '…' : 'Enregistrer'}
            </button>
          )}
          {selected && !isNew && (
            <>
              <button className="me-btn" disabled={busy} onClick={onDupliquer}>
                Dupliquer
              </button>
              <button className="me-btn me-btn-danger" disabled={busy} onClick={onSupprimer}>
                Supprimer
              </button>
            </>
          )}
        </div>

        <div className="me-grid">
          <label className="me-f-full">
            <span>Nom du plat *</span>
            <input type="text" value={form.title} disabled={!formActive} onChange={(e) => set('title', e.target.value)} />
          </label>

          <fieldset className="me-f-full">
            <legend>Menus (cumulables)</legend>
            {MENU_TYPES.map((m) => (
              <label key={m.slug} className="me-check">
                <input
                  type="checkbox"
                  disabled={!formActive}
                  checked={form.menuTypes.includes(m.slug)}
                  onChange={() => toggleInArray('menuTypes', m.slug)}
                />
                {m.label}
              </label>
            ))}
          </fieldset>

          <label>
            <span>Plat</span>
            <select value={form.course} disabled={!formActive} onChange={(e) => set('course', e.target.value)}>
              <option value="">— Aucun —</option>
              {COURSES.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Rang dans la catégorie</span>
            <input
              type="number"
              min={0}
              step={1}
              value={form.position}
              disabled={!formActive}
              onChange={(e) => set('position', Number(e.target.value) || 0)}
            />
          </label>

          <label className="me-check me-f-inline">
            <input
              type="checkbox"
              disabled={!formActive}
              checked={form.isCourseNote}
              onChange={(e) => set('isCourseNote', e.target.checked)}
            />
            Note de cours (Formule)
          </label>

          <label className="me-f-full">
            <span>Description</span>
            <textarea
              rows={2}
              value={form.description}
              disabled={!formActive}
              onChange={(e) => set('description', e.target.value)}
            />
          </label>

          <label>
            <span>Prix</span>
            <input
              type="text"
              placeholder="16€"
              value={form.price}
              disabled={!formActive}
              onChange={(e) => set('price', e.target.value)}
            />
          </label>

          <label className="me-f-full">
            <span>Allergènes</span>
            <textarea
              rows={2}
              placeholder="Gluten, Lait, Œufs…"
              value={form.allergens}
              disabled={!formActive}
              onChange={(e) => set('allergens', e.target.value)}
            />
          </label>

          <fieldset className="me-f-full">
            <legend>Régimes alimentaires</legend>
            {DIETARY_TAGS.map((tag) => (
              <label key={tag} className="me-check">
                <input
                  type="checkbox"
                  disabled={!formActive}
                  checked={form.dietary.includes(tag)}
                  onChange={() => toggleInArray('dietary', tag)}
                />
                {tag}
              </label>
            ))}
          </fieldset>
        </div>
      </div>
    </div>
  );
}
